import { Dispatch, FC, SetStateAction } from "react";
import PopupModal from "../../../UI/Popup";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../../store";
import { Link, useNavigate } from "react-router";
import Decline from "../../../UI/Icons/Decline";
import CustomButton from "../../../UI/Buttons/CustomButton";
import RoundedTick from "../../../UI/Icons/RoundedTick";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../../store/application/slice";
import {
  declineEnquiryThunk,
  getAllActiveCentresThunk,
  getAllPendingEnquiriesThunk,
  getEnquiryApproveThunk,
} from "../../../../store/superadmin/thunks";
import { AUTH_ROUTE } from "../../../../pages/App/routes";
import { ICentreDetailsNew } from "../../../../store/superadmin/types";
import { isFulfilled } from "@reduxjs/toolkit";

interface ReviewCentreProps {
  showModal: boolean;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  pendingReviewPage: number;
  pendingReviewPerPage: number;
  selectedKeyword: string;
}

const ReviewCentre: FC<ReviewCentreProps> = ({
  showModal,
  setShowModal,
  pendingReviewPage,
  pendingReviewPerPage,
  selectedKeyword,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const inProgress = useSelector((state) => state.superadmin.inProgress);
  const declineInProgress = useSelector(
    (state) => state.superadmin.declineInProgress
  );
  const getEnquiryDetails = useSelector(
    (state) => state.superadmin.enquiryDetails
  );

  const onClickClose = () => {
    setShowModal(false);
  };

  const handleDecline = async (id: number) => {
    await dispatch(declineEnquiryThunk({ enquiry_id: id }));
    setShowModal(false);
    dispatch(showTransparentPreloaderAction());
    await dispatch(
      getAllPendingEnquiriesThunk({
        keyword: "",
        page: pendingReviewPage,
        per_page: pendingReviewPerPage,
      })
    );
    dispatch(hideTransparentPreloaderAction());
  };

  const handleApproveEnquiry = async (id: number) => {
    const result = await dispatch(getEnquiryApproveThunk({ enquiry_id: id }));
    if (isFulfilled(result)) {
      setShowModal(!true);
      await dispatch(
        getAllPendingEnquiriesThunk({
          keyword: "",
          page: pendingReviewPage,
          per_page: pendingReviewPerPage,
        })
      );
      dispatch(showTransparentPreloaderAction());
      await dispatch(
        getAllActiveCentresThunk({
          keyword: selectedKeyword,
          page: pendingReviewPage,
          per_page: pendingReviewPerPage,
        })
      );
      dispatch(hideTransparentPreloaderAction());
    }
    setShowModal(false);
  };

  const redirectToSetupNewCentre = () => {
    setShowModal(false);
    navigate(AUTH_ROUTE.ADMIN_SETUP_NEW_CENTRE);
  };

  return (
    <>
      <PopupModal
        visible={showModal}
        showCloseBtn={true}
        onClickClose={onClickClose}
        customWidth={900}
      >
        <div className="d-flex flex-column gap-15 text-break">
          <div className="fs-22 fw-400 text-black">Review Centre Enquiry</div>
          <div className="fs-16 fw-700 text-black">Primary Contact</div>
          <div className="d-flex gap-40 gap-mobile-20 flex-wrap">
            <div className="d-flex flex-column gap-5">
              <div className="fs-13 fw-400 text-grey-medium">First Name</div>
              <div className="fs-16 fw-400 text-black">
                {getEnquiryDetails?.first_name}
              </div>
            </div>
            <div className="d-flex flex-column gap-5">
              <div className="fs-13 fw-400 text-grey-medium">Last Name</div>
              <div className="fs-16 fw-400 text-black">
                {getEnquiryDetails?.last_name}
              </div>
            </div>
            <div className="d-flex flex-column gap-5">
              <div className="fs-13 fw-400 text-grey-medium">Phone Number</div>
              <div className="fs-16 fw-400 text-black">
                {getEnquiryDetails?.phone_number}
              </div>
            </div>
            <div className="d-flex flex-column gap-5">
              <div className="fs-13 fw-400 text-grey-medium">Email</div>
              <div className="fs-16 fw-400 text-black">
                {getEnquiryDetails?.email}
              </div>
            </div>
          </div>
          <div className="w-100 h-1px bg-off-white"></div>
          <div className="fs-16 fw-700 text-black">Centre Information</div>
          {getEnquiryDetails &&
            getEnquiryDetails !== null &&
            getEnquiryDetails.enquiry?.map(
              (getEnquiryDetail: ICentreDetailsNew, index: number) => (
                <>
                  <div key={index}>
                    <div className="d-flex gap-40 gap-mobile-20 flex-wrap">
                      <div className="d-flex flex-column gap-5">
                        <div className="fs-13 fw-400 text-grey-medium">
                          Business Name
                        </div>
                        <div className="fs-16 fw-400 text-black">
                          {getEnquiryDetail?.business_name}
                        </div>
                      </div>
                      <div className="d-flex flex-column gap-5">
                        <div className="fs-13 fw-400 text-grey-medium">
                          Website
                        </div>
                        <div className="fs-16 fw-400 text-black">
                          {getEnquiryDetail?.website ? (
                            <div className="text-decoration-underline">
                              <Link
                                to={getEnquiryDetail?.website}
                                target="_blank"
                                className="fs-16 fw-400 text-black"
                              >
                                {getEnquiryDetail?.website}
                              </Link>
                            </div>
                          ) : (
                            <div className="fs-16 fw-400 text-black">-</div>
                          )}
                        </div>
                      </div>
                      <div className="d-flex flex-column gap-5">
                        <div className="fs-13 fw-400 text-grey-medium">
                          Phone Number
                        </div>
                        <div className="fs-16 fw-400 text-black">
                          {getEnquiryDetail?.center_phone ?? `-`}
                        </div>
                      </div>
                      <div className="d-flex flex-column gap-5">
                        <div className="fs-13 fw-400 text-grey-medium">
                          Address
                        </div>
                        <div className="fs-16 fw-400 text-black">
                          {getEnquiryDetail?.address ?? `-`}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="w-100 h-1px bg-off-white"></div>
                </>
              )
            )}

          <div className="fs-16 fw-700 text-black">Comment</div>
          <div className="fs-16 fw-400 text-black">
            {getEnquiryDetails?.comment ?? `-`}
          </div>
          <div className="h-10px w-100"></div>
          <div className="d-flex flex-wrap gap-10 justify-content-between">
            <CustomButton
              variant={"primary"}
              type="button"
              isLoading={declineInProgress}
              contentClass="d-flex"
              className="btn-theme bg-off-white text-black mobile-w-100"
              onClick={() =>
                handleDecline(getEnquiryDetails?.primary_contact_id ?? 0)
              }
            >
              <Decline /> Decline and Delete Enquiry
            </CustomButton>
            <CustomButton
              variant={"primary"}
              type="button"
              isLoading={inProgress}
              contentClass="d-flex"
              className="btn-theme bg-teal text-black mobile-w-100"
              onClick={redirectToSetupNewCentre}
            >
              <RoundedTick color={inProgress ? "#aaaaaa" : "#000000"} />
              Set Up Approved Centres
            </CustomButton>
          </div>
        </div>
      </PopupModal>
    </>
  );
};

export default ReviewCentre;
